<script lang="ts" setup>
  import { UIFormItem, UIInput, UIInputGroup, UIRadioGroup, UIRadioGroupItem, UISpace, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { PropType, ref, watch } from 'vue'
  import {
    ELEMENTS_META,
    ELEMENTS_STYLES,
    IElement,
    UIBuilderSpacing,
    getMarginProperties,
    getPaddingProperties,
    onChangeMarginPadding,
    MediaSelector,
    DigitalDelivery,
    ContentTypes,
  } from '@gohighlevel/ghl-proposals-common'
  import { useElementOption } from '../../../composable/elementOptions'
  import ColorPickerComponent from '../../common/ColorPickerComponent.vue'
  import { useI18n } from 'vue-i18n'
  import { useAppStore } from '@/store/app'

  const appStore = useAppStore()
  const { t } = useI18n()
  const i18nLocale = useI18n()
  const props = defineProps({
    element: Object as PropType<IElement>,
  })

  const { updateOption, updateStyles } = useElementOption()

  const bgColor = ref(props.element?.responsiveStyles?.large?.backgroundColor)
  const margin = ref(getMarginProperties(props.element))
  const padding = ref(getPaddingProperties(props.element))

  watch(props, _newValue => {
    if (props.element) {
      bgColor.value = props.element?.responsiveStyles?.large?.backgroundColor
      margin.value = getMarginProperties(props.element)
      padding.value = getPaddingProperties(props.element)
    }
  })

  function onSelectImage(image: any) {
    if (!image) return
    updateOption(ELEMENTS_META.SRC, image.url)
  }

  const positiveNumberRegex = /^\d*\.?\d{0,2}$/
  const rules = {
    height: {
      trigger: ['input', 'blur', 'change'],
      required: true,
      validator() {
        const heightProps = props.element?.responsiveStyles?.large?.height

        if (!heightProps && props.element?.component.options.src) {
          return new Error(t('validations.required.height'))
        }
        if (heightProps && (!positiveNumberRegex.test(heightProps) || Number(heightProps) <= 0)) {
          return new Error(t('validations.invalid.positiveNumbers'))
        }
        return true
      },
    },
    width: {
      trigger: ['input', 'blur', 'change'],
      required: true,
      validator() {
        const widthProps = props.element?.responsiveStyles?.large?.width

        if (!widthProps && props.element?.component.options.src) {
          return new Error(t('validations.required.width'))
        }
        if (widthProps && (!positiveNumberRegex.test(widthProps) || Number(widthProps) <= 0)) {
          return new Error(t('validations.invalid.positiveNumbers'))
        }
        return true
      },
    },
  }

  /*
   * height and width should not be empty.
   * if its empty the calculation of offsite in PDF generation will be wrong and because of that,
   * the content will be eliminated from the PDF - So we should have the height & width
   */
  const updateHeightOrWidth = (key: string, value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      updateStyles(key, '')
      return
    }
    const valStr = String(value).trim()
    updateStyles(key, valStr)
  }
  const onBoxStyleChange = onChangeMarginPadding(updateStyles)

  const onSelectedMedia = (data: DigitalDelivery) => {
    if (!data.url) return
    onSelectImage(data)
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content">
    <UIContentWrap :locale="(i18nLocale?.locale?.value as any) || 'en-US'" class="p-0" style="display: contents">
      <UIFormItem :label="$t('common.imageUrl')">
        <UIInputGroup>
          <UIInput
            id="image-src"
            :model-value="element?.component.options.src"
            @update:model-value="val => updateOption(ELEMENTS_META.SRC, val)"
          />

          <MediaSelector
            :handshake="appStore.handshake"
            :location-id="appStore.document?.locationId"
            :company-id="appStore.companyId"
            @selected-media="onSelectedMedia"
            :allowed-extensions="['image']"
            :content-type="ContentTypes.IMAGE"
          />
        </UIInputGroup>
      </UIFormItem>
    </UIContentWrap>
    <!-- <UIFormItem label="Image Alt Text">
      <UIInput
        id="image-alt-text"
        :model-value="element?.component.options.altText"
        @update:model-value="val => updateOption(ELEMENTS_META.ALT_TEXT, val)"
      />
    </UIFormItem>
    <UIFormItem label="Link">
      <UIInput
        id="image-action-link"
        :model-value="element?.component.options.href"
        placeholder="www.example.com"
        @update:model-value="val => updateOption(ELEMENTS_META.HREF, val)"
      >
        <template #prefix>https://</template>
      </UIInput>
    </UIFormItem> -->
    <UIFormItem :label="$t('common.align')">
      <UIRadioGroup :value="element?.responsiveStyles.large.align" @update:value="val => updateStyles(ELEMENTS_STYLES.ALIGN, val)">
        <UISpace>
          <UIRadioGroupItem value="img-left">
            <template #content>{{ $t('common.left') }}</template>
          </UIRadioGroupItem>
          <UIRadioGroupItem value="img-center">
            <template #content>{{ $t('common.center') }}</template>
          </UIRadioGroupItem>
          <UIRadioGroupItem value="img-right">
            <template #content>{{ $t('common.right') }}</template>
          </UIRadioGroupItem>
        </UISpace>
      </UIRadioGroup>
    </UIFormItem>

    <UIFormItem :label="$t('common.imageEffect')">
      <UIRadioGroup
        :value="element?.responsiveStyles.large.imageEffect"
        @update:value="val => updateStyles(ELEMENTS_STYLES.IMAGE_EFFECT, val)"
      >
        <UISpace>
          <UIRadioGroupItem value="img-full-color">
            <template #content>{{ $t('common.fullColor') }}</template>
          </UIRadioGroupItem>
          <UIRadioGroupItem value="img-black-white">
            <template #content>{{ $t('common.blackAndWhite') }}</template>
          </UIRadioGroupItem>
        </UISpace>
      </UIRadioGroup>
    </UIFormItem>

    <ColorPickerComponent
      v-model="bgColor"
      :label="$t('common.backgroundColor')"
      @update:modelValue="updateStyles(ELEMENTS_STYLES.BACKGROUND_COLOR, bgColor, props.element?.id, props.element?.type)"
    />

    <UIContentWrap :locale="(i18nLocale?.locale?.value as any) || 'en-US'" class="p-0" style="display: contents">
      <UIFormItem :label="$t('common.height')" :rule="rules.height">
        <UIInput
          id="image-height"
          :model-value="element?.responsiveStyles.large.height"
          placeholder=""
          @update:modelValue="val => updateHeightOrWidth(ELEMENTS_STYLES.HEIGHT, val)"
        >
          <template #suffix>{{ $t('common.px') }}</template>
        </UIInput>
      </UIFormItem>
    </UIContentWrap>
    <UIContentWrap :locale="(i18nLocale?.locale?.value as any) || 'en-US'" class="p-0" style="display: contents">
      <UIFormItem :label="$t('common.width')" :rule="rules.width">
        <UIInput
          id="image-width"
          :model-value="element?.responsiveStyles.large.width"
          placeholder=""
          @update:model-value="val => updateHeightOrWidth(ELEMENTS_STYLES.WIDTH, val)"
        >
          <template #suffix>{{ $t('common.px') }}</template>
        </UIInput>
      </UIFormItem>
    </UIContentWrap>
    <UIBuilderSpacing :element="props.element" @onUpdate="onBoxStyleChange" />
  </div>
</template>

<style lang="scss">
  .hl-proposal-sidebar-content {
    .hl-radio-group-item-action {
      display: none;
    }

    .hl-radio-group-item.n-radio-button {
      padding: 5px;
    }
  }
</style>
